import React, { useEffect, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { Spinner } from '@/components/ui'
import { useAuthStore } from '@/features/auth/authStore'
import { useWebPubSub } from '@/features/notification'
import { useLogout } from '@/hooks/useAuth'
import { ROUTES } from '@/utils/routes'

/**
 * Invite page: processes logout when the user lands here with an existing session
 * (e.g. from an invite link). Shows only a loading screen, then redirects to login.
 *
 * - If auth token exists: disconnects WebPubSub and runs logout (same as Sidebar),
 *   then redirects to /login (handled by logout mutation).
 * - If no token: redirects to /login immediately.
 */
const InvitePage: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const storeToken = useAuthStore((s) => s.token)
  const webPubSubClient = useWebPubSub()
  const processedRef = useRef(false)

  const hasToken = !!localStorage.getItem('token') || !!storeToken

  // If there's a Clerk ticket in the URL, redirect to Sign Up so the Clerk SDK processes it.
  // Otherwise, fallback to Login, sending along the query parameters in either case.
  const destination = location.search.includes('__clerk_ticket')
    ? `${ROUTES.SIGN_UP}${location.search}`
    : `${ROUTES.LOGIN}${location.search}`

  const logoutMutation = useLogout({ redirectTo: destination })

  useEffect(() => {
    if (processedRef.current) return
    processedRef.current = true

    const run = async () => {
      if (hasToken) {
        try {
          void webPubSubClient.disconnect()
          await logoutMutation.mutateAsync()
          // Navigation to destination is done in useLogout mutationFn
        } catch {
          // On logout error, still send user to the destination so they are not stuck
          navigate(destination, { replace: true })
        }
      } else {
        navigate(destination, { replace: true })
      }
    }

    void run()
  }, [hasToken, navigate, logoutMutation, webPubSubClient, destination])

  return (
    <div
      className="flex min-h-screen items-center justify-center bg-[var(--Colors-Background-bg-primary)]"
      aria-live="polite"
      aria-busy="true"
    >
      <Spinner className="size-8 text-[var(--Colors-Foreground-fg-primary)]" />
    </div>
  )
}

export default InvitePage
